// Fraîcheur des données: un seul mécanisme pour que tous les chiffres suivent.
//
// Chaque écran charge ses données lui-même (pas de cache partagé). Sans
// signal commun, un paiement enregistré depuis Finances laissait la page
// Factures, l'accueil ou un autre onglet sur des chiffres figés jusqu'au
// rechargement manuel. Ici:
//   1. toute écriture réussie vers le serveur (intercepteur de `lib/api.js`,
//      y compris le rejeu de la file hors ligne) appelle `signalerEcriture`;
//   2. l'adresse est traduite en « domaines » touchés par la table
//      `DEPENDANCES` (un paiement touche les factures, la finance, le crédit
//      client, l'accueil et les rapports…);
//   3. l'événement `ecarbu:donnees-modifiees` part dans l'onglet et vers les
//      autres onglets (BroadcastChannel, sinon l'événement `storage`);
//   4. le hook `useRafraichirSur` recharge les écrans abonnés à ces domaines.

export const EVENEMENT_DONNEES_MODIFIEES = 'ecarbu:donnees-modifiees';
export const TOUS_DOMAINES = '*';
const CANAL = 'ecarbu-donnees';
const CLE_STOCKAGE = 'ecarbu:donnees-modifiees';

// Ce qu'une écriture sur une ressource rend potentiellement faux ailleurs.
// Volontairement large: recharger un écran de trop coûte une requête, en
// oublier un laisse un chiffre faux à l'écran.
const ACCUEIL = ['accueil', 'rapports', 'alertes'];
export const DEPENDANCES = {
  invoices: ['factures', 'paiements', 'finance', 'clients', 'commandes', ...ACCUEIL],
  finance: ['finance', 'paiements', 'factures', 'clients', 'trajets', 'depenses', ...ACCUEIL],
  clients: ['clients', 'factures', 'commandes', 'finance', 'accueil'],
  orders: ['commandes', 'repartition', 'trajets', 'factures', 'stock', 'clients', 'finance', ...ACCUEIL],
  trips: [
    'trajets', 'stock', 'commandes', 'repartition', 'convois', 'camions', 'chauffeurs',
    'finance', 'depenses', 'locations', 'lots', ...ACCUEIL,
  ],
  operations: ['convois', 'trajets', 'repartition', 'commandes', 'camions', 'chauffeurs', ...ACCUEIL],
  // Lots attribués (lot 057) : confirmer un camion crée un trajet, son stock et son chauffeur.
  lots: ['lots', 'trajets', 'stock', 'repartition', 'camions', 'chauffeurs', 'finance', ...ACCUEIL],
  stock: ['stock', 'trajets', 'commandes', ...ACCUEIL],
  shortages: ['trajets', 'stock', 'finance', 'depenses', ...ACCUEIL],
  'proof-of-delivery': ['trajets', 'commandes', 'stock', 'documents', ...ACCUEIL],
  'arbitration-certificates': ['trajets', 'documents', ...ACCUEIL],
  'mission-integrity': ['trajets', 'chauffeurs', ...ACCUEIL],
  trucks: [
    'camions', 'repartition', 'maintenance', 'locations', 'chauffeurs', 'finance', 'depenses',
    'documents', ...ACCUEIL,
  ],
  'charge-rules': ['camions', 'depenses', 'finance', ...ACCUEIL],
  observations: ['depenses', 'camions', 'trajets', 'finance', 'convois', ...ACCUEIL],
  drivers: ['chauffeurs', 'repartition', 'camions', 'documents', ...ACCUEIL],
  companies: ['societes', 'camions', 'chauffeurs', 'repartition'],
  rentals: ['locations', 'camions', 'factures', 'finance', 'clients', 'repartition', ...ACCUEIL],
  'inbound-rentals': ['locations', 'camions', 'finance', 'depenses', 'repartition', ...ACCUEIL],
  maintenance: ['maintenance', 'camions', 'documents', 'repartition', 'finance', 'depenses', ...ACCUEIL],
  documents: ['documents', 'camions', 'chauffeurs', 'maintenance', 'trajets', ...ACCUEIL],
  alerts: ['alertes', 'accueil'],
  'fleet-reports': ['rapports', 'finance', 'accueil'],
  'monthly-close': ['rapports', 'finance', 'accueil'],
  'daily-reports': ['rapports'],
  organizations: ['rapports', 'finance', 'parametres', 'accueil'],
  'org-settings': ['parametres', 'alertes', 'accueil'],
  stations: ['stations', 'stock', 'finance', ...ACCUEIL],
  'control-fuel': ['controle', 'stock', 'accueil'],
  'pricing-rules': ['tarifs', 'commandes'],
  'geo-locations': ['lieux', 'trajets'],
  'ecarbu-score': ['trajets', 'chauffeurs', 'accueil'],
  users: ['utilisateurs'],
  licenses: ['licences'],
  demo: [TOUS_DOMAINES],
};

// Types d'entité de la file hors ligne → ressource de la table ci-dessus.
const ENTITES_FILE = {
  invoice: 'invoices',
  finance_operation: 'finance',
  client: 'clients',
  order: 'orders',
  trip: 'trips',
  trip_update: 'trips',
  operation: 'operations',
  stock_movement: 'stock',
  truck: 'trucks',
  driver: 'drivers',
  company: 'companies',
  station: 'stations',
};

// Requêtes POST qui ne modifient rien (calcul, aperçu, contrôle) ou qui
// partent toutes seules (positions GPS, jeton): les signaler ferait recharger
// des écrans en boucle.
const SANS_EFFET = [
  /^\/users\/login/,
  /^\/auth\//,
  /^\/users\/me\/(change-password|regenerate-access-code|preferences)/,
  /^\/users\/me$/,
  /:preview$/,
  /\/fleet-check$/,
  /^\/tracking\/batch/,
  /^\/tracking\/providers\/[^/]+\/diagnostics/,
  /^\/integrations\//,
  /^\/backend-ops\//,
  /^\/pricing-rules\/resolve/,
  /^\/sync\/full/,
];

const ECRITURES = new Set(['post', 'put', 'patch', 'delete']);

const cheminRelatif = (url) => {
  let chemin = String(url || '');
  const api = chemin.indexOf('/api/v1');
  if (api >= 0) chemin = chemin.slice(api + '/api/v1'.length);
  chemin = chemin.split('?')[0].split('#')[0];
  if (!chemin.startsWith('/')) chemin = `/${chemin}`;
  return chemin.length > 1 ? chemin.replace(/\/+$/, '') : chemin;
};

const unique = (liste) => [...new Set(liste)];

/** Domaines touchés par une écriture sur `url` (liste vide: rien à signaler). */
export function domainesPourEcriture(url, corps) {
  const chemin = cheminRelatif(url);
  if (SANS_EFFET.some((motif) => motif.test(chemin))) return [];
  const ressource = chemin.split('/')[1] || '';
  if (ressource === 'sync') {
    let lot = corps;
    if (typeof lot === 'string') {
      try {
        lot = JSON.parse(lot);
      } catch (erreur) {
        lot = null;
      }
    }
    const items = Array.isArray(lot?.items) ? lot.items : [];
    const domaines = items.flatMap((item) => DEPENDANCES[ENTITES_FILE[item?.entity_type]] || []);
    // Un lot dont on ne sait rien dire: tout recharger plutôt que rien.
    return domaines.length ? unique(domaines) : [TOUS_DOMAINES];
  }
  return DEPENDANCES[ressource] ? [...DEPENDANCES[ressource]] : [];
}

let canal = null;
let canalInitialise = false;
const ouvrirCanal = () => {
  if (canalInitialise) return canal;
  canalInitialise = true;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      canal = new BroadcastChannel(CANAL);
      canal.onmessage = (message) => {
        const domaines = message?.data?.domaines;
        if (Array.isArray(domaines)) diffuserLocalement(domaines, true);
      };
    }
  } catch (erreur) {
    canal = null;
  }
  if (!canal && typeof window !== 'undefined') {
    window.addEventListener('storage', (evenement) => {
      if (evenement.key !== CLE_STOCKAGE || !evenement.newValue) return;
      try {
        const { domaines } = JSON.parse(evenement.newValue);
        if (Array.isArray(domaines)) diffuserLocalement(domaines, true);
      } catch (erreur) {
        // Valeur illisible écrite par une autre version: on l'ignore.
      }
    });
  }
  return canal;
};

function diffuserLocalement(domaines, distant = false) {
  if (typeof window === 'undefined' || !window.dispatchEvent) return;
  window.dispatchEvent(
    new CustomEvent(EVENEMENT_DONNEES_MODIFIEES, { detail: { domaines, distant } }),
  );
}

/** Annonce que des données ont changé (dans cet onglet et les autres). */
export function signalerDonneesModifiees(domaines) {
  const liste = unique((domaines || []).filter(Boolean));
  if (!liste.length) return;
  diffuserLocalement(liste);
  const c = ouvrirCanal();
  try {
    if (c) c.postMessage({ domaines: liste });
    else if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        CLE_STOCKAGE,
        JSON.stringify({ domaines: liste, t: Date.now(), r: Math.random() }),
      );
    }
  } catch (erreur) {
    // Le relais entre onglets est un confort: l'onglet courant est déjà servi.
  }
}

/** Appelé par l'intercepteur axios après une réponse réussie. */
export function signalerEcriture(config) {
  const methode = String(config?.method || 'get').toLowerCase();
  if (!ECRITURES.has(methode)) return;
  signalerDonneesModifiees(domainesPourEcriture(config?.url, config?.data));
}

/** Écoute les autres onglets dès le premier abonnement. */
export function ecouterAutresOnglets() {
  ouvrirCanal();
}

// Pour les tests: repartir d'un état neuf.
export function _reinitialiserCanal() {
  try {
    canal?.close?.();
  } catch (erreur) {
    // rien
  }
  canal = null;
  canalInitialise = false;
}
